import { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import sidebarData from "./SidebarData";
import SimpleBar from "simplebar-react";
import MetisMenu from "metismenujs";
import withRouter from "../../components/Common/withRouter";
import { Link } from "react-router-dom";
import { withTranslation } from "react-i18next";

const Sidebar = (props) => {
  const [userlvl, setUserlvl] = useState(0);
  const ref = useRef();

  // --- Récupération du rôle de l'utilisateur ---
  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem("authUser"));
    if (userData) setUserlvl(userData.user_ROLE_ID);
  }, []);

  // --- Gestion activation dropdown ---
  const activateParentDropdown = useCallback((item) => {
    item.classList.add("active");
    const parent = item.parentElement;
    const parent2El = parent?.childNodes[1];
    if (parent2El && parent2El.id !== "side-menu") parent2El.classList.add("mm-show");

    let currentParent = parent;
    while (currentParent) {
      currentParent.classList?.add("mm-active");
      const ul = currentParent.parentElement;
      if (ul) ul.classList?.add("mm-show");
      currentParent = ul?.parentElement;
      if (currentParent?.tagName !== "LI") break;
    }
    scrollElement(item);
  }, []);

  const removeActivation = (items) => {
    for (let item of items) {
      item.classList.remove("active", "mm-active");
      const parent = item.parentElement;
      const sub = parent?.childNodes[1];
      if (sub && sub.id !== "side-menu") sub.classList.remove("mm-show");
      parent?.classList.remove("mm-active");
    }
  };

  const findDataByPath = useCallback((path, data) => {
    for (const item of data) {
      const itemPath = item.link || item.url;
      if (itemPath === path) return item;
      if (item.subItem) {
        const found = findDataByPath(path, item.subItem);
        if (found) return found;
        for (const sub of item.subItem) {
          if (sub.subMenu) {
            const deep = findDataByPath(path, sub.subMenu);
            if (deep) return deep;
          }
        }
      }
    }
    return null;
  }, []);

  // --- Active menu en fonction de l'URL ---
  const activeMenu = useCallback(() => {
    const fullPath = props.router.location.pathname;
    const search = props.router.location.search || "";
    const isFromVariant = search.includes("from=variant");
    const ul = document.getElementById("side-menu-item");
    if (!ul) return;
    const items = ul.getElementsByTagName("a");
    removeActivation(items);

    let bestMatch = null;

    for (let domItem of items) {
      const itemPath = domItem.pathname;
      const menuItemData = findDataByPath(itemPath, sidebarData);
      let currentMatchLength = 0;

      // Si on est dans le flux Variant (?from=variant), on veut que "/ixivariant" corresponde,
      // et on veut empêcher "/chapterManage" (Procédures [WORK]) de matcher.
      if (isFromVariant) {
        if (itemPath === "/ixivariant") {
          if (fullPath.startsWith("/languageManageWork/") || fullPath.startsWith("/homemono/")) {
            currentMatchLength = fullPath.length;
          }
        }
        if (itemPath === "/chapterManage") {
          continue;
        }
      }

      // Vérification des chemins principaux
      if (fullPath.startsWith(itemPath)) currentMatchLength = itemPath.length;

      // Vérification des chemins supplémentaires
      if (menuItemData?.activeOnAdditionalPaths) {
        for (const additionalPath of menuItemData.activeOnAdditionalPaths) {
          if (fullPath.startsWith(additionalPath) && additionalPath.length > currentMatchLength)
            currentMatchLength = additionalPath.length;
        }
      }

      if (currentMatchLength > 0 && (!bestMatch || currentMatchLength > bestMatch.matchLength)) {
        bestMatch = { item: domItem, matchLength: currentMatchLength };
      }
    }

    if (bestMatch) activateParentDropdown(bestMatch.item);
  }, [props.router.location.pathname, props.router.location.search, activateParentDropdown, findDataByPath]);

  useEffect(() => ref.current?.recalculate(), []);
  useEffect(() => {
    new MetisMenu("#side-menu-item");
    activeMenu();
  }, []);
  useEffect(() => activeMenu(), [activeMenu]);

  function scrollElement(item) {
    if (item) {
      const pos = item.offsetTop;
      if (pos > window.innerHeight) ref.current.getScrollElement().scrollTop = pos - 300;
    }
  }

  const renderSidebar = (data) => {
    let hideAdminBlock = false;

    return data.map((item, key) => {
      // --- Filtrage Administration ---
      if (item.isAdminBlock && userlvl !== 1 && userlvl !== 2) {
        hideAdminBlock = true;
        return null;
      }
      if (hideAdminBlock) {
        if (item.isMainMenu) hideAdminBlock = false; // fin du bloc admin
        else return null;
      }
      // --- Filtrage Super Administration ---
      if (item.isSuperAdminBlock && userlvl !== 1) {
        hideAdminBlock = true;
        return null;
      }
      // --- Filtrage par rôle sur un item précis ---
      if (item.roles && !item.roles.includes(userlvl)) {
        return null;
      }

      // --- Titre principal ---
      if (item.isMainMenu) {
        return (
          <li key={key} className="menu-title text-primary text-center">
            {item.icon && <i className={item.icon} style={{ marginRight: "5px" }}></i>}
            {props.t(item.label)}
          </li>
        );
      }

      // --- Menu normal / avec sous-items ---
      return (
        <li key={key} title={item.title || ""}>
          <Link to={item.url || "/#"} className={item.isHasArrow ? "" : "has-arrow"}>
            {item.icon && <i className={item.icon} style={{ marginRight: "5px" }}></i>}
            <span>{props.t(item.label)}</span>
          </Link>
          {item.subItem && (
            <ul className="sub-menu">
              {item.subItem.map((subItem, subKey) => (
                <li key={subKey} title={subItem.title || ""}>
                  <Link to={subItem.link || "#"} className={subItem.subMenu ? "has-arrow waves-effect" : ""}>
                    {subItem.icon && <i className={subItem.icon} style={{ marginRight: "5px" }}></i>}
                    {props.t(subItem.sublabel)}
                  </Link>
                  {subItem.subMenu && (
                    <ul className="sub-menu">
                      {subItem.subMenu.map((subSubMenu, subSubKey) => (
                        <li key={subSubKey}>
                          <Link to={subSubMenu.link || "#"}>
                            {subSubMenu.icon && <i className={subSubMenu.icon} style={{ marginRight: "5px" }}></i>}
                            {props.t(subSubMenu.title)}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          )}
        </li>
      );
    });
  };



  return (
    <div className="vertical-menu">
      <SimpleBar className="h-100" ref={ref}>
        <div id="sidebar-menu">
          <ul className="metismenu list-unstyled" id="side-menu-item">
            {renderSidebar(sidebarData)}
          </ul>



        </div>
      </SimpleBar>
    </div>
  );
};

Sidebar.propTypes = {
  location: PropTypes.object,
  t: PropTypes.any,
};

export default withRouter(withTranslation()(Sidebar));


//Creators : Florian Cohen-Joly @3DDGAMES -- Under license